import { useCallback, useEffect, useMemo, useState } from "react";

import {
  IntegrationLayoutEntry,
  MetadataEnabledState,
  MetadataKeys,
  MetadataParentKey,
} from "@common/models/integrations";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAdministrationDeps } from "@main/pages/Administration/Administration";

import { useIntegrationLayout } from "./useIntegrationLayout";

const emptyEnabledState: MetadataEnabledState = { ticket_metadata: {}, app_user_metadata: {} };

const useIntegrationLayoutSettings = (initialLayout: IntegrationLayoutEntry[], selectedAppId: string) => {
  const { selectedWorkspace } = useAppSelectionContext();
  const { administrationService } = useAdministrationDeps();
  const [metadataKeys, setMetadataKeys] = useState<MetadataKeys>();
  const [metadataEnabledState, setMetadataEnabledState] = useState<MetadataEnabledState>(emptyEnabledState);
  const [initialMetadataEnabledState, setInitialMetadataEnabledState] =
    useState<MetadataEnabledState>(emptyEnabledState);

  const layout = useIntegrationLayout(initialLayout);

  useEffect(() => {
    if (!selectedWorkspace) return;
    administrationService.getMetadataKeys(selectedWorkspace.id, selectedAppId).then(({ data }) => {
      setMetadataKeys(data);
      const ticketEntry = initialLayout.find((e) => e.key === "ticket_metadata");
      const appUserEntry = initialLayout.find((e) => e.key === "app_user_metadata");
      const loaded: MetadataEnabledState = {
        ticket_metadata: Object.fromEntries(
          (data.ticket_metadata ?? []).map((k) => [k, ticketEntry?.metadata_keys?.includes(k) ?? false]),
        ),
        app_user_metadata: Object.fromEntries(
          (data.app_user_metadata ?? []).map((k) => [k, appUserEntry?.metadata_keys?.includes(k) ?? false]),
        ),
      };
      setMetadataEnabledState(loaded);
      setInitialMetadataEnabledState(loaded);
    });
  }, [selectedWorkspace, selectedAppId, administrationService]);

  const toggleMetadataKey = useCallback((parentKey: MetadataParentKey, key: string) => {
    setMetadataEnabledState((prev) => ({
      ...prev,
      [parentKey]: { ...prev[parentKey], [key]: !prev[parentKey][key] },
    }));
  }, []);

  const toggleAllMetadataKeys = useCallback((parentKey: MetadataParentKey, keys: string[]) => {
    setMetadataEnabledState((prev) => {
      const allEnabled = keys.every((k) => prev[parentKey][k]);
      return {
        ...prev,
        [parentKey]: Object.fromEntries(keys.map((k) => [k, !allEnabled])),
      };
    });
  }, []);

  const setMetadataKeysEnabled = useCallback((parentKey: MetadataParentKey, newState: Record<string, boolean>) => {
    setMetadataEnabledState((prev) => ({ ...prev, [parentKey]: newState }));
  }, []);

  const resetMetadataKeys = useCallback((parentKey: MetadataParentKey) => {
    setMetadataEnabledState((prev) => ({
      ...prev,
      [parentKey]: Object.fromEntries(Object.keys(prev[parentKey]).map((k) => [k, false])),
    }));
  }, []);

  const hasMetadataKeyChanges = useMemo(() => {
    const changed = (current: Record<string, boolean>, initial: Record<string, boolean>) =>
      Object.keys(current).some((k) => current[k] !== initial[k]);
    return (
      changed(metadataEnabledState.ticket_metadata, initialMetadataEnabledState.ticket_metadata) ||
      changed(metadataEnabledState.app_user_metadata, initialMetadataEnabledState.app_user_metadata)
    );
  }, [metadataEnabledState, initialMetadataEnabledState]);

  const integrationLayout = useMemo(
    () =>
      layout.integrationLayout.map((entry) => {
        if (entry.key === "ticket_metadata" || entry.key === "app_user_metadata") {
          const enabledKeys = Object.entries(metadataEnabledState[entry.key as MetadataParentKey])
            .filter(([, v]) => v)
            .map(([k]) => k);
          return { ...entry, metadata_keys: enabledKeys };
        }
        return entry;
      }),
    [layout.integrationLayout, metadataEnabledState],
  );

  return {
    ...layout,
    integrationLayout,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  };
};

export { useIntegrationLayoutSettings };
